import { useMemo, useState } from "react";

const ReportCard = ({ data = [] }) => {
  const [selectedDate, setSelectedDate] = useState("");

  // Always show these zones even if they have 0 data
  const predefinedZones = ["zone2", "zone4"];

  // Filter data by selected date
  const filteredData = useMemo(() => {
    if (!selectedDate) return data;
    return data.filter((item) => {
      if (!item.createdAt) return false;
      const itemDate = new Date(item.createdAt).toISOString().slice(0, 10);
      return itemDate === selectedDate;
    });
  }, [data, selectedDate]);

  // Compute report based on filtered data
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const report = useMemo(() => {
    if (!data.length) return null;

    // Combine predefined zones with those in dataset
    const allZones = Array.from(
      new Set([...predefinedZones, ...data.map((item) => item.zoneId || "Unknown")])
    );

    // Total rentalCharge for filtered data
    const totalrentalCharge = filteredData.reduce(
      (sum, item) => sum + (parseFloat(item.rentalCharge) || 0),
      0
    );

    // Unique units overall (filtered)
    const uniqueUnitsOverall = new Set(filteredData.map((item) => item.unitNumber)).size;

    // Group filtered data by zoneId
    const zoneMap = {};
    filteredData.forEach((item) => {
      const zoneId = item.zoneId || "Unknown";
      const rentalCharge = parseFloat(item.rentalCharge) || 0;
      const unit = item.unitNumber;

      if (!zoneMap[zoneId]) {
        zoneMap[zoneId] = { rentalCharge: 0, units: new Set() };
      }
      zoneMap[zoneId].rentalCharge += rentalCharge;
      zoneMap[zoneId].units.add(unit);
    });

    // Build final zones array (including empty ones)
    const zones = allZones.map((zoneId) => {
      const info = zoneMap[zoneId] || { rentalCharge: 0, units: new Set() };
      const formattedZone =
        zoneId.startsWith("zone") && zoneId.length > 4
          ? `Zone ${zoneId.slice(4)}`
          : zoneId;

      return {
        zone: formattedZone,
        rentalCharge: info.rentalCharge,
        percentage: totalrentalCharge
          ? ((info.rentalCharge / totalrentalCharge) * 100).toFixed(2)
          : "0.00",
        units: info.units.size,
      };
    });

    return { totalrentalCharge, uniqueUnits: uniqueUnitsOverall, zones };
  }, [data, filteredData]);

  return (
    <div className="p-5 rounded-md border bg-white shadow-md w-full max-w-md">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold uppercase text-gray-800">Report</h2>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:outline-none focus:ring focus:ring-blue-300"
        />
      </div>

      {report ? (
        <div className="text-gray-700 space-y-2">
          <p className="font-semibold text-lg">Jumlah Caj</p>

          <p className="text-base">
            Jumlah:{" "}
            <span className="font-medium text-blue-700">
              RM{" "}
              {report.totalrentalCharge.toLocaleString(undefined, {
                minimumFractionDigits: 2,
              })}
            </span>
          </p>

          <p className="text-base">
            Jumlah Unit:{" "}
            <span className="font-medium text-blue-700">
              {report.uniqueUnits} Unit
            </span>
          </p>

          <hr className="my-3 border-gray-300" />

          {/* Zones summary */}
          <div className="space-y-1">
            {report.zones.map((z) => (
              <p key={z.zone}>
                {z.zone}:{" "}
                <span className="font-medium text-green-700">
                  RM{" "}
                  {z.rentalCharge.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                  })}
                </span>{" "}
                | <span className="text-gray-600">{z.percentage}%</span> |{" "}
                <span className="text-gray-600">{z.units} Unit(s)</span>
              </p>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-gray-500 text-sm">
          No data available for selected date
        </p>
      )}
    </div>
  );
};

export default ReportCard;
